
import { v4 as uuidv4 } from "uuid";
import { 
  IStringNumHash,
  Feature,
  FeatureUserRating,
  FloatingFeature,
} from "@models";
import { 
  Character,
  DamageMultiplier,
  UpgradableNumber,
  TemplateBase,
  AbilityScoresUpgradable,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export class Summon {
  true_id = "";
  name = "";
  description = "";
  connected = false;
  image_url = "";
  // home
  challenge_rating: UpgradableNumber = new UpgradableNumber();
  creature_type = ""; // Beast, Dragon, Humanoid, etc.
  subtypes: string[] = []; // Drow, Bronze, etc.
  hit_dice_size: UpgradableNumber = new UpgradableNumber();
  hit_dice_count: UpgradableNumber = new UpgradableNumber();
  max_hit_points: UpgradableNumber = new UpgradableNumber();
  temp_hit_points: UpgradableNumber = new UpgradableNumber();
  initiative_modifier: UpgradableNumber = new UpgradableNumber();
  armor_class: UpgradableNumber = new UpgradableNumber();
  size = "";
  alignment = "";
  // attributes
  speed: IStringNumHash = {};
  senses: IStringNumHash = {};
  passives: {
    passive_perception: UpgradableNumber,
    passive_insight: UpgradableNumber,
    passive_investigation: UpgradableNumber
  } = {
    passive_perception: new UpgradableNumber(),
    passive_insight: new UpgradableNumber(),
    passive_investigation: new UpgradableNumber()
  };
  condition_immunities: string[] = [];
  damage_multipliers: DamageMultiplier[] = [];
  tool_proficiencies: string[] = [];
  
  skill_proficiencies_upgradable: { _id: string, name: string, value: UpgradableNumber }[] = [];
  saving_throws: AbilityScoresUpgradable = new AbilityScoresUpgradable()
  languages = "";
  
  ability_scores: AbilityScoresUpgradable = new AbilityScoresUpgradable();

  actions: Feature[] = [];
  legendary_actions: Feature[] = [];
  special_abilities: Feature[] = [];

  constructor(obj?: any, log_it = false) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.creature_type = obj.creature_type ? (obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : common_util.capitalize_firsts(obj.type)) : "";
      this.subtypes = obj.subtypes ? obj.subtypes : [];
      if (this.subtypes.length === 0 && obj.subtype) {
        const subtype = obj.subtype ? common_util.capitalize_firsts(obj.subtype) : "";
        if (subtype !== "") {
          this.subtypes.push(subtype);
        }
      }
      this.challenge_rating = obj.challenge_rating ? new UpgradableNumber(obj.challenge_rating) : new UpgradableNumber();
      this.image_url = obj.image_url ? obj.image_url : "";
      this.ability_scores = obj.ability_scores ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable();
      this.actions = [];
      if (obj.actions && obj.actions.length > 0) {
        for (const o of obj.actions) {
          const feature = new Feature(o);
          feature.parent_type = "summon";
          feature.parent_id = this.true_id;
          feature.base_id = this.true_id;
          this.actions.push(feature);
        }
      }
      this.legendary_actions = [];
      if (obj.legendary_actions && obj.legendary_actions.length > 0) {
        for (const o of obj.legendary_actions) {
          const feature = new Feature(o);
          feature.parent_type = "summon";
          feature.parent_id = this.true_id;
          feature.base_id = this.true_id;
          this.legendary_actions.push(feature);
        }
      }
      this.special_abilities = [];
      if (obj.special_abilities && obj.special_abilities.length > 0) {
        for (const o of obj.special_abilities) {
          const feature = new Feature(o);
          feature.parent_type = "summon";
          feature.parent_id = this.true_id;
          feature.base_id = this.true_id;
          this.special_abilities.push(feature);
        }
      }
      this.hit_dice_size = obj.hit_dice_size ? new UpgradableNumber(obj.hit_dice_size) : new UpgradableNumber();
      this.hit_dice_count = obj.hit_dice_count ? new UpgradableNumber(obj.hit_dice_count) : new UpgradableNumber();
      if (log_it) {
        debugger;
      }
      this.max_hit_points = obj.max_hit_points ? new UpgradableNumber(obj.max_hit_points) : new UpgradableNumber();
      this.temp_hit_points = obj.temp_hit_points ? new UpgradableNumber(obj.temp_hit_points) : new UpgradableNumber();
      this.speed = obj.speed ? obj.speed : {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0,
        hover: 0
      };
      if (log_it) {
        debugger;
      }
      this.passives = obj.passives ? {
        passive_perception: obj.passives.passive_perception ? new UpgradableNumber(obj.passives.passive_perception) : new UpgradableNumber(),
        passive_insight: obj.passives.passive_insight ? new UpgradableNumber(obj.passives.passive_insight) : new UpgradableNumber(),
        passive_investigation: obj.passives.passive_investigation ? new UpgradableNumber(obj.passives.passive_investigation) : new UpgradableNumber()
      } : {
        passive_perception: new UpgradableNumber(),
        passive_insight: new UpgradableNumber(),
        passive_investigation: new UpgradableNumber()
      };
      this.saving_throws = obj.saving_throws ? new AbilityScoresUpgradable(obj.saving_throws) : new AbilityScoresUpgradable();
      this.languages = obj.languages ? obj.languages : "";
      // if (this.name.includes("Despair")) {
        // console.log(obj);
      // }
      this.tool_proficiencies = obj.tool_proficiencies && obj.tool_proficiencies.length ? [...obj.tool_proficiencies] : [];
      this.skill_proficiencies_upgradable = [];
      if (obj.skill_proficiencies_upgradable) {
        obj.skill_proficiencies_upgradable.forEach((upg: any) => {
          this.skill_proficiencies_upgradable.push({ _id: upg._id, name: upg.name, value: new UpgradableNumber(upg.value) });
        });
      } else if (obj.skill_proficiencies) {
        Object.keys(obj.skill_proficiencies).forEach((name: string) => {
          this.skill_proficiencies_upgradable.push({ _id: "", name, value: new UpgradableNumber(obj.skill_proficiencies[name]) });
        });
      }
      this.initiative_modifier = obj.initiative_modifier ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
      this.armor_class = obj.armor_class ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
      this.size = obj.size ? obj.size : "Medium";
      this.alignment = obj.alignment ? common_util.capitalize_firsts(obj.alignment) : "Neutral";
      this.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
      this.damage_multipliers = [];
      if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
        obj.damage_multipliers.forEach((dm: any) => {
          this.damage_multipliers.push(new DamageMultiplier(dm));
        });
      }
      this.senses = {};
      if (obj.senses) {
        if (obj.senses.length > 0) {
          for (const sense of obj.senses) {
            // console.log(sense);
            this.senses[sense.name] = sense.range;
            // this.senses.push(new SenseFeature(sense));
          }
        } else {
          this.senses = obj.senses;
        }
      }
      this.connected = false;
      if (log_it) {
        debugger;
      }
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      creature_type: this.creature_type,
      subtypes: this.subtypes,
      image_url: this.image_url,
      ability_scores: this.ability_scores.toDBObj(),
      hit_dice_size: this.hit_dice_size.toDBObj(),
      hit_dice_count: this.hit_dice_count.toDBObj(),
      actions: this.actions.map(o => o.toDBObj()),
      legendary_actions: this.legendary_actions.map(o => o.toDBObj()),
      special_abilities: this.special_abilities.map(o => o.toDBObj()),
      challenge_rating: this.challenge_rating.toDBObj(),
      max_hit_points: this.max_hit_points.toDBObj(),
      temp_hit_points: this.temp_hit_points.toDBObj(),
      speed: this.speed,
      passives: { 
        passive_perception: this.passives.passive_perception.toDBObj(),
        passive_insight: this.passives.passive_insight.toDBObj(),
        passive_investigation: this.passives.passive_investigation.toDBObj()
      },
      senses: this.senses,
      saving_throws: this.saving_throws.toDBObj(),
      skill_proficiencies_upgradable: this.skill_proficiencies_upgradable.map(upg => { return { _id: upg._id, name: upg.name, value: upg.value.toDBObj() } }),
      tool_proficiencies: this.tool_proficiencies,
      initiative_modifier: this.initiative_modifier.toDBObj(),
      armor_class: this.armor_class.toDBObj(),
      size: this.size,
      alignment: this.alignment,
      condition_immunities: this.condition_immunities,
      damage_multipliers: this.damage_multipliers.map(o => o.toDBObj()),
      languages: this.languages
    };
  }

  hit_dice_string(char: Character): string {
    const response = `${char_util.value(this.hit_dice_count, { obj: char, item_id: "" })}d${char_util.value(this.hit_dice_size,{ obj: char, item_id: "" })}`;
    return response;
  }

  get skills_string(): string {
    let response = "";
    this.skill_proficiencies_upgradable.forEach(upg => {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${upg.name}: ${upg.value.getFormula()}`;
    });
    return response;
  }

  get tools_string(): string {
    return this.tool_proficiencies.join(", ");
  }

  get senses_string(): string {
    let response = "";
    Object.keys(this.senses).forEach(key => {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${key} ${this.senses[key]} ft.`;
    });
    if (!this.passives.passive_perception.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `Passive Perception: ${this.passives.passive_perception.getFormula()}`;
    }
    if (!this.passives.passive_insight.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `Passive Insight: ${this.passives.passive_insight.getFormula()}`;
    }
    if (!this.passives.passive_investigation.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `Passive Investigation: ${this.passives.passive_investigation.getFormula()}`;
    }
    return response;
  }

  get saving_throws_string(): string {
    let response = "";
    if (!this.saving_throws.STR.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `STR: ${this.saving_throws.STR.getFormula()}`;
    }
    if (!this.saving_throws.DEX.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `DEX: ${this.saving_throws.DEX.getFormula()}`;
    }
    if (!this.saving_throws.CON.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `CON: ${this.saving_throws.CON.getFormula()}`;
    }
    if (!this.saving_throws.INT.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `INT: ${this.saving_throws.INT.getFormula()}`;
    }
    if (!this.saving_throws.WIS.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `WIS: ${this.saving_throws.WIS.getFormula()}`;
    }
    if (!this.saving_throws.CHA.isBlank()) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `CHA: ${this.saving_throws.CHA.getFormula()}`;
    }
    return response;
  }

  get speed_string(): string {
    let response = "";
    Object.keys(this.speed).forEach(key => {
      if (this.speed[key]) {
        if (response.length > 0) {
          response += ", ";
        }
        if (key === "hover") {
          if (this.speed[key] === 1) {
            response += "hover";
          } else {
            response += "no hover";
          }
        } else {
          response += `${key} ${this.speed[key]} ft.`;
        }
      }
    });
    return response;
  }

  get immunities_string(): string {
    let return_me = "";
    this.damage_multipliers.filter(o => o.multiplier === 0).forEach(dm => {
      dm.damage_types.forEach(dt => {
        if (return_me.length > 0) {
          return_me += ", ";
        }
        return_me += dt;
      });
    });
    return return_me;
  }

  get resistances_string(): string {
    let return_me = "";
    this.damage_multipliers.filter(o => o.multiplier === 0.5).forEach(dm => {
      dm.damage_types.forEach(dt => {
        if (return_me.length > 0) {
          return_me += ", ";
        }
        return_me += dt;
      });
    });
    return return_me;
  }

  get vulnerabilities_string(): string {
    let return_me = "";
    this.damage_multipliers.filter(o => o.multiplier === 2).forEach(dm => {
      dm.damage_types.forEach(dt => {
        if (return_me.length > 0) {
          return_me += ", ";
        }
        return_me += dt;
      });
    });
    return return_me;
  }

  get condition_immunities_string(): string {
    let return_me = "";
    this.condition_immunities.forEach(ci => {
      if (return_me.length > 0) {
        return_me += ", ";
      }
      return_me += ci;
    });
    return return_me;
  }

  copy(copyMe: Summon, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.creature_type = copyMe.creature_type;
    this.connected = copyMe.connected;
    this.ability_scores = copyMe.ability_scores.clone();
    this.image_url = copyMe.image_url;
    this.subtypes = [...copyMe.subtypes];
    this.hit_dice_size = copyMe.hit_dice_size.clone();
    this.hit_dice_count = copyMe.hit_dice_count.clone();
    this.challenge_rating = copyMe.challenge_rating.clone();
    this.max_hit_points = copyMe.max_hit_points.clone();
    this.temp_hit_points = copyMe.temp_hit_points.clone();
    this.speed = {...copyMe.speed};
    this.passives = {
      passive_perception: copyMe.passives.passive_perception.clone(),
      passive_insight: copyMe.passives.passive_insight.clone(),
      passive_investigation: copyMe.passives.passive_investigation.clone()
    };
    this.saving_throws = copyMe.saving_throws.clone();
    // this.skill_proficiencies = {...copyMe.skill_proficiencies};
    this.skill_proficiencies_upgradable = copyMe.skill_proficiencies_upgradable.map(upg => {
      return { _id: upg._id, name: upg.name, value: upg.value.clone() };
    });
    this.tool_proficiencies = [...copyMe.tool_proficiencies];
    this.initiative_modifier = copyMe.initiative_modifier.clone();
    this.armor_class = copyMe.armor_class.clone();
    this.actions = copyMe.actions.map(o => o.clone(drop_ids));
    this.legendary_actions = copyMe.legendary_actions.map(o => o.clone(drop_ids));
    this.special_abilities = copyMe.special_abilities.map(o => o.clone(drop_ids));
    this.senses = { ...copyMe.senses };
    this.size = copyMe.size;
    this.alignment = copyMe.alignment;
    this.condition_immunities = [...copyMe.condition_immunities];
    this.damage_multipliers = copyMe.damage_multipliers.map(o => o.clone(drop_ids));
    this.languages = copyMe.languages;
  }

  clone(drop_ids = false): Summon {
    const new_obj = new Summon();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyTemplate(copyMe: TemplateBase, all_floating_features: FloatingFeature[]): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    const obj = {...copyMe.item};
    this.creature_type = obj ? (obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : common_util.capitalize_firsts(obj.type)) : "";
    this.subtypes = obj && obj.subtypes ? obj.subtypes : [];
    if (this.subtypes.length === 0 && obj && obj.subtype) {
      const subtype = obj ? common_util.capitalize_firsts(obj.subtype) : "";
      if (subtype !== "") {
        this.subtypes.push(subtype);
      }
    }
    this.challenge_rating = obj ? new UpgradableNumber(obj.challenge_rating) : new UpgradableNumber();
    this.image_url = obj && obj.image_url ? obj.image_url : "";
    this.ability_scores = obj ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable();
    this.actions = [];
    if (obj && obj.actions && obj.actions.length > 0) {
      for (const o of obj.actions) {
        const floating_feature = all_floating_features.find(o2 => o2._id === o.floating_feature_id);
        if (floating_feature) {
          const new_feature = new Feature();
          new_feature.floating_feature_id = floating_feature._id;
          new_feature.floating_feature = floating_feature;
          this.actions.push(new_feature);
        }
      }
    }
    this.legendary_actions = [];
    if (obj && obj.legendary_actions && obj.legendary_actions.length > 0) {
      for (const o of obj.legendary_actions) {
        const floating_feature = all_floating_features.find(o2 => o2._id === o.floating_feature_id);
        if (floating_feature) {
          const new_feature = new Feature();
          new_feature.floating_feature_id = floating_feature._id;
          new_feature.floating_feature = floating_feature;
          this.legendary_actions.push(new_feature);
        }
      }
    }
    this.special_abilities = [];
    if (obj && obj.special_abilities && obj.special_abilities.length > 0) {
      for (const o of obj.special_abilities) {
        const floating_feature = all_floating_features.find(o2 => o2._id === o.floating_feature_id);
        if (floating_feature) {
          const new_feature = new Feature();
          new_feature.floating_feature_id = floating_feature._id;
          new_feature.floating_feature = floating_feature;
          this.special_abilities.push(new_feature);
        }
      }
    }
    this.hit_dice_size = obj ? new UpgradableNumber(obj.hit_dice_size) : new UpgradableNumber();
    this.hit_dice_count = obj ? new UpgradableNumber(obj.hit_dice_count) : new UpgradableNumber();
    this.max_hit_points = obj ? new UpgradableNumber(obj.max_hit_points) : new UpgradableNumber();
    this.temp_hit_points = obj.temp_hit_points ? new UpgradableNumber(obj.temp_hit_points) : new UpgradableNumber();
    this.speed = obj ? obj.speed : {
      walk: 0,
      swim: 0,
      climb: 0,
      fly: 0,
      burrow: 0,
      hover: 0
    };
    this.passives = obj && obj.passives ? {
      passive_perception: obj.passives.passive_perception ? new UpgradableNumber(obj.passives.passive_perception) : new UpgradableNumber(),
      passive_insight: obj.passives.passive_insight ? new UpgradableNumber(obj.passives.passive_insight) : new UpgradableNumber(),
      passive_investigation: obj.passives.passive_investigation ? new UpgradableNumber(obj.passives.passive_investigation) : new UpgradableNumber()
    } : {
      passive_perception: new UpgradableNumber(),
      passive_insight: new UpgradableNumber(),
      passive_investigation: new UpgradableNumber()
    };
    this.saving_throws = obj && obj.saving_throws ? 
      new AbilityScoresUpgradable(obj.saving_throws) : 
      new AbilityScoresUpgradable();
    this.languages = obj ? obj.languages : "";
    this.tool_proficiencies = obj ? obj.tool_proficiencies : [];
    this.skill_proficiencies_upgradable = [];
    if (obj.skill_proficiencies_upgradable) {
      obj.skill_proficiencies_upgradable.forEach((upg: any) => {
        this.skill_proficiencies_upgradable.push({ _id: upg._id, name: upg.name, value: new UpgradableNumber(upg.value) });
      });
    } else if (obj.skill_proficiencies) {
      Object.keys(obj.skill_proficiencies).forEach((name: string) => {
        this.skill_proficiencies_upgradable.push({ _id: "", name, value: new UpgradableNumber(obj.skill_proficiencies[name]) });
      });
    }
    this.initiative_modifier = obj ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
    this.armor_class = obj ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
    this.size = obj ? obj.size : "Medium";
    this.alignment = obj ? common_util.capitalize_firsts(obj.alignment) : "Neutral";
    this.condition_immunities = obj ? obj.condition_immunities : [];
    this.damage_multipliers = [];
    if (obj && obj.damage_multipliers && obj.damage_multipliers.length > 0) {
      obj.damage_multipliers.forEach((dm: any) => {
        this.damage_multipliers.push(new DamageMultiplier(dm));
      });
    }
    this.senses = {};
    if (obj && obj.senses) {
      if (obj.senses.length > 0) {
        obj.senses.forEach((sense: any) => {
          // console.log(sense);
          this.senses[sense.name] = sense.range;
          // this.senses.push(new SenseFeature(sense));
        });
      } else {
        this.senses = { ...obj.senses };
      }
    }
    this.connected = false;
  }

  connectUserRatings(userRatings: FeatureUserRating[]) {
    this.actions.forEach(feature => {
      feature.connectUserRatings(userRatings);
    });
    this.legendary_actions.forEach(feature => {
      feature.connectUserRatings(userRatings);
    });
    this.special_abilities.forEach(feature => {
      feature.connectUserRatings(userRatings);
    });
  }
}